<template>
  <div class="container-fluid user-details">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ $t('user_details.success') }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ $t('user_details.error') }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <div class="row mt-3">
      <!-- Left Section -->
      <div class="col-md-8">
        <div class="card">
          <div class="card-header text-white bg-purple">{{ $t('user_details.title') }}</div>
          <div class="card-body">
            <div class="row">
              <!-- First Name -->
              <div class="col-md-6 mb-3">
                <label for="firstName" class="form-label">{{ $t('first_name') }}</label>
                <input
                    type="text"
                    class="form-control"
                    id="firstName"
                    v-model="user.firstName"
                    :disabled="isReadOnlyMode"
                    readonly
                >
              </div>
              <!-- Last Name -->
              <div class="col-md-6 mb-3">
                <label for="lastName" class="form-label">{{ $t('last_name') }}</label>
                <input
                    type="text"
                    class="form-control"
                    id="lastName"
                    v-model="user.lastName"
                    :disabled="isReadOnlyMode"
                    readonly
                >
              </div>
              <!-- Username -->
              <div class="col-md-6 mb-3">
                <label for="userLogin" class="form-label">{{ $t('username') }}</label>
                <input
                    type="text"
                    class="form-control"
                    id="userLogin"
                    v-model="user.username"
                    :disabled="isReadOnlyMode"
                    readonly
                >
              </div>
              <!-- Email -->
              <div class="col-md-6 mb-3">
                <label for="userEmail" class="form-label">{{ $t('email') }}</label>
                <input
                    type="email"
                    class="form-control"
                    id="userEmail"
                    v-model="user.email"
                    :disabled="isReadOnlyMode"
                    readonly
                >
              </div>
            </div>

            <div class="row">
              <!-- Location -->
              <div class="col-md-6 mb-3">
                <label for="location" class="form-label">{{ $t('location') }}</label>
                <select
                    class="form-select"
                    id="location"
                    v-model="user.location"
                    :disabled="isReadOnlyMode"
                >
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">
                    {{ loc.name }}
                  </option>
                </select>
              </div>
              <!-- Department -->
              <div class="col-md-6 mb-3">
                <label for="department" class="form-label">{{ $t('department') }}</label>
                <select
                    class="form-select"
                    id="department"
                    v-model="user.department"
                    :disabled="isReadOnlyMode"
                >
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="row">
              <!-- Position -->
              <div class="col-md-6 mb-3">
                <label for="position" class="form-label">{{ $t('position') }}</label>
                <select
                    class="form-select"
                    id="position"
                    v-model="user.position"
                    :disabled="isReadOnlyMode"
                >
                  <option v-for="pos in positions" :key="pos.id" :value="pos.id">
                    {{ pos.name }}
                  </option>
                </select>
              </div>
              <!-- Manager -->
              <div class="col-md-6 mb-3">
                <label for="manager" class="form-label">{{ $t('manager') }}</label>
                <select
                    class="form-select"
                    id="manager"
                    v-model="user.manager"
                    :disabled="isReadOnlyMode"
                >
                  <option v-for="manager in users" :key="manager.id" :value="manager.id">
                    {{ manager.first_name }} {{ manager.last_name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="row">
              <!-- Employment Start Date -->
              <div class="col-md-6 mb-3">
                <label for="startDate" class="form-label">{{ $t('employment_start') }}</label>
                <input
                    type="date"
                    class="form-control"
                    id="startDate"
                    v-model="user.employmentStart"
                    :disabled="isReadOnlyMode"
                >
              </div>
              <!-- Employment End Date -->
              <div class="col-md-6 mb-3">
                <label for="endDate" class="form-label">{{ $t('employment_end') }}</label>
                <input
                    type="date"
                    class="form-control"
                    id="endDate"
                    v-model="user.employmentEnd"
                    :disabled="isReadOnlyMode"
                >
              </div>
              <!-- Birth Date -->
              <div class="col-md-6 mb-3">
                <label for="birthDate" class="form-label">{{ $t('date_of_birth') }}</label>
                <input
                    type="date"
                    class="form-control"
                    id="birthDate"
                    v-model="user.birthDate"
                    :disabled="!canEditBirthDate && isReadOnlyMode"
                >
              </div>
            </div>

            <!-- Save Changes Button -->
            <div class="row">
              <div class="col-md-12">
                <button
                    @click="saveChanges"
                    class="btn btn-primary custom-button"
                    :disabled="!canEditBirthDate && isReadOnlyMode"
                >
                  {{ $t('save') }}
                </button>
              </div>

              <!-- User Calendar Link -->
              <div class="row mt-3">
                <div class="col-md-12 text-center">
                  <router-link
                      :to="{
                      path: '/vacation-calendar/',
                      query: { user_ids: $route.params.id }
                    }"
                      class="btn btn-secondary"
                  >
                    {{ $t('userCalendar') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Section for Statistics -->
      <div class="col-md-4">
        <div class="card">
          <div class="card-header text-white bg-purple">{{ $t('statistics') }}</div>
          <div class="card-body">
            <table class="table table-unbordered">
              <tbody>
              <tr>
                <td colspan="2" class="fw-bold border-top">{{ $t('vacationStatistics') }}</td>
              </tr>
              <tr>
                <td>{{ $t('quota') }}</td>
                <td>{{ statistics.vacationQuota }}</td>
              </tr>
              <tr>
                <td>{{ $t('currentlyAvailable') }}</td>
                <td>{{ statistics.vacationCurrentDate }}</td>
              </tr>
              <tr>
                <td>{{ $t('usedDays') }}</td>
                <td>{{ statistics.vacationUsed }}</td>
              </tr>
              <tr>
                <td>{{ $t('availableDays') }}</td>
                <td>{{ statistics.vacationAvailable }}</td>
              </tr>

              <tr>
                <td colspan="2" class="fw-bold border-top">{{ $t('sickLeaveStatistics') }}</td>
              </tr>
              <tr>
                <td>{{ $t('quota') }}</td>
                <td>{{ statistics.sickLeaveQuota }}</td>
              </tr>
              <tr>
                <td>{{ $t('usedDays') }}</td>
                <td>{{ statistics.sickLeaveUsed }}</td>
              </tr>
              <tr>
                <td>{{ $t('availableDays') }}</td>
                <td>{{ statistics.sickLeaveAvailable }}</td>
              </tr>

              <tr>
                <td colspan="2" class="fw-bold border-top">{{ $t('otherAbsences') }}</td>
              </tr>
              <tr>
                <td>{{ $t('recoveryDays') }}</td>
                <td>{{ statistics.recoveryDays }}</td>
              </tr>
              <tr>
                <td>{{ $t('unpaidAbsence') }}</td>
                <td>{{ statistics.unpaidAbsences }}</td>
              </tr>
              <tr>
                <td colspan="2" class="fw-bold border-top">{{ $t('transferredDayStatistics') }}</td>
              </tr>
              <tr>
                <td> {{ $t('quota') }}</td>
                <td>{{ statistics.transferredDays }}</td>
              </tr>
              <tr>
                <td>{{ $t('usedDays') }}</td>
                <td>{{ statistics.transferredUsed }}</td>
              </tr>
              <tr>
                <td>{{ $t('availableDays') }}</td>
                <td>{{ statistics.transferredDaysLeft }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'UserDetail',
  data() {
    return {
      user: {
        id: '',
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        location: '',
        department: '',
        position: '',
        manager: '',
        employmentStart: '',
        employmentEnd: '',
        birthDate: '',
      },
      statistics: {
        vacationQuota: 20,
        vacationCurrentDate: 0,
        vacationUsed: 0,
        vacationAvailable: 0,
        sickLeaveQuota: 10,
        sickLeaveUsed: 0,
        sickLeaveAvailable: 10,
        recoveryDays: 0,
        unpaidAbsences: 0,
        transferredDays: 0,
        transferredUsed: 0,
        transferredDaysLeft: 0,
        transferDeadline: null
      },
      locations: [],
      departments: [],
      positions: [],
      users: [],
      successMessage: '',
      errorMessage: '',
    };
  },
  computed: {
    store: () => useMainStore(),
    isEditingSelf() {
      return String(this.store.user?.id) === this.$route.params.id;
    },
    isSuperUser() {
      return this.store.user?.is_superuser;
    },
    hasAdminEditPermission() {
      return !!this.store.user?.permissions?.includes('users.edit_all_users');
    },
    isReadOnlyMode() {
      return !(this.isSuperUser || this.hasAdminEditPermission);
    },
    canEditBirthDate() {
      return (
          this.isEditingSelf &&
          this.store.user?.permissions?.includes('users.change_own_birthdate')
      );
    },
  },

  mounted() {
    this.fetchUserData();
    this.fetchLocations();
    this.fetchDepartments();
    this.fetchPositions();
    this.fetchUsers();
    this.fetchStatistics();
  },
  methods: {
    fetchUserData() {
      axios
          .get(`/api/v1/admin/users/${this.$route.params.id}/`)
          .then(response => {
            this.user = {
              firstName: response.data.first_name,
              lastName: response.data.last_name,
              username: response.data.username,
              email: response.data.email,
              location: response.data.location ? response.data.location.id : '',
              department: response.data.department ? response.data.department.id : '',
              position: response.data.position ? response.data.position.id : '',
              manager: response.data.manager ? response.data.manager.id : null,
              employmentStart: response.data.employment_start,
              employmentEnd: response.data.employment_end,
              birthDate: response.data.date_of_birth,
            };
          })
          .catch(() => {
            this.errorMessage = 'Failed to fetch user details';
            setTimeout(() => (this.errorMessage = ''), 3000);
          });
    },
    async fetchLocations() {
      try {
        let url = '/api/v1/admin/locations/';
        const allLocations = [];

        while (url) {
          const response = await axios.get(url);
          const data = response.data;

          allLocations.push(...(data.results || []));

          if (data.next) {
            const nextUrl = new URL(data.next, window.location.origin);
            url = nextUrl.pathname + nextUrl.search;
          } else {
            url = null;
          }
        }

        this.locations = allLocations;
      } catch (error) {
        this.errorMessage = 'Failed to fetch locations';
        setTimeout(() => (this.errorMessage = ''), 3000);
      }
    },
    fetchDepartments() {
      axios
          .get('/api/v1/admin/departments/')
          .then(response => {
            this.departments = response.data.results;
          })
          .catch(() => {
            this.errorMessage = 'Failed to fetch departments';
            setTimeout(() => (this.errorMessage = ''), 3000);
          });
    },
    async fetchPositions() {
      try {
        let url = '/api/v1/admin/positions/';
        const allPositions = [];

        while (url) {
          const response = await axios.get(url);
          const data = response.data;

          allPositions.push(...(data.results || []));

          if (data.next) {
            const nextUrl = new URL(data.next, window.location.origin);
            url = nextUrl.pathname + nextUrl.search;
          } else {
            url = null;
          }
        }

        this.positions = allPositions;
      } catch (error) {
        this.errorMessage = 'Failed to fetch positions';
        setTimeout(() => (this.errorMessage = ''), 3000);
      }
    },
    async fetchUsers() {
      try {
        let url = '/api/v1/admin/users/?page=1';
        const allUsers = [];

        while (url) {
          const response = await axios.get(url);
          const data = response.data;

          allUsers.push(...(data.results || []));

          if (data.next) {
            const nextUrl = new URL(data.next, window.location.origin);
            url = nextUrl.pathname + nextUrl.search;
          } else {
            url = null;
          }
        }

        this.users = allUsers;
      } catch (error) {
        this.errorMessage = 'Failed to fetch users';
        setTimeout(() => (this.errorMessage = ''), 3000);
      }
    },
    fetchStatistics() {
      const userId = this.$route.params.id;
      if (!userId) return;

      axios
          .get(`/api/v1/vacation/absences/statistics/`, {
            params: {user_ids: userId},
          })
          .then(response => {
            this.statistics = response.data;
          })
          .catch(() => {
            this.errorMessage = 'Failed to fetch statistics';
            setTimeout(() => (this.errorMessage = ''), 3000);
          });
    },
    saveChanges() {
      axios
          .patch(`/api/v1/admin/users/${this.$route.params.id}/`, {
            first_name: this.user.firstName,
            last_name: this.user.lastName,
            email: this.user.email,
            location: this.user.location,
            department: this.user.department,
            position: this.user.position,
            manager: this.user.manager,
            employment_start: this.user.employmentStart,
            employment_end: this.user.employmentEnd,
            date_of_birth: this.user.birthDate,
          })
          .then(() => {
            this.successMessage = 'User details updated successfully';
            setTimeout(() => (this.successMessage = ''), 3000);
          })
          .catch(() => {
            this.errorMessage = 'Failed to save user details';
            setTimeout(() => (this.errorMessage = ''), 3000);
          });
    },
  },
};
</script>

<style scoped>
.bg-purple {
  background-color: #540054;
}

.card {
  margin-bottom: 15px;
}

.user-details .col-md-8 {
  margin-top: 6vh;
}

.user-details .col-md-4 {
  margin-top: 6vh;
}
</style>
